// src/domains/onboarding/pages/ProfessionalSignupPage.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from 'infrastructure/firebase';
import { isValidEmail, isValidPhone } from 'shared/utils/security';

const T = {
  brand: '#6B5876', accent: '#C9929A', border: '#E8E5EC',
  s2: '#FAF7FB', s3: '#F0EDF3', text: '#3D3447', text2: '#6B6070', muted: '#A8A1B0',
  sans: "'DM Sans',sans-serif", serif: "'Instrument Serif',serif",
};

const inputStyle = {
  width: '100%', padding: '11px 14px', border: `1.5px solid ${T.border}`,
  borderRadius: 12, fontSize: 14, fontFamily: T.sans, outline: 'none',
  boxSizing: 'border-box', color: T.text, background: '#fff',
};

const btnPrimary = (disabled) => ({
  width: '100%', padding: 14, background: disabled ? T.muted : T.brand,
  color: '#fff', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 500,
  cursor: disabled ? 'not-allowed' : 'pointer', fontFamily: T.sans,
  opacity: disabled ? 0.7 : 1,
});

const SPECIALTIES = [
  { id: 'esteticista', label: 'Esteticista' },
  { id: 'massoterapeuta', label: 'Massoterapeuta' },
  { id: 'fisioterapeuta', label: 'Fisioterapeuta' },
  { id: 'nutricionista', label: 'Nutricionista' },
  { id: 'psicologo', label: 'Psicólogo(a)' },
  { id: 'medico', label: 'Médico(a)' },
  { id: 'dentista', label: 'Dentista' },
  { id: 'personal', label: 'Personal Trainer' },
  { id: 'barbeiro', label: 'Barbeiro(a)' },
  { id: 'cabeleireiro', label: 'Cabeleireiro(a)' },
  { id: 'maquiador', label: 'Maquiador(a)' },
  { id: 'outro', label: 'Outro' },
];

export default function ProfessionalSignupPage() {
  const navigate = useNavigate();
  const [step, setStep]       = useState(1); // 1=dados profissionais, 2=acesso, 3=sucesso
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);

  // Passo 1 — dados profissionais
  const [nome, setNome]           = useState('');
  const [email, setEmail]         = useState('');
  const [telefone, setTelefone]   = useState('');
  const [especialidade, setEspec] = useState('');
  const [outraEspec, setOutraEspec] = useState('');

  // Passo 2 — senha
  const [senha, setSenha]   = useState('');
  const [senha2, setSenha2] = useState('');

  const isMobile = window.innerWidth < 768;

  const validateStep1 = () => {
    if (!nome.trim() || nome.trim().length < 2) return 'Informe seu nome completo.';
    if (!isValidEmail(email))                   return 'E-mail inválido.';
    if (telefone && !isValidPhone(telefone))     return 'Telefone inválido.';
    if (!especialidade)                          return 'Selecione sua especialidade.';
    if (especialidade === 'outro' && !outraEspec.trim()) return 'Descreva sua especialidade.';
    return null;
  };

  const handleStep1 = (e) => {
    e.preventDefault();
    setError('');
    const err = validateStep1();
    if (err) { setError(err); return; }
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (senha.length < 6)   { setError('A senha deve ter no mínimo 6 caracteres.'); return; }
    if (senha !== senha2)   { setError('As senhas não coincidem.'); return; }

    setLoading(true);
    try {
      const especFinal = especialidade === 'outro' ? outraEspec.trim() : (
        SPECIALTIES.find(s => s.id === especialidade)?.label || especialidade
      );
      await authService.register({
        name:      nome.trim(),
        email:     email.trim().toLowerCase(),
        password:  senha,
        role:      'professional',
        phone:     telefone.trim(),
        specialty: especFinal,
      });
      setStep(3);
    } catch (err) {
      if (err?.code === 'auth/email-already-in-use') {
        setError('Este e-mail já possui cadastro. Faça login ou use outro e-mail.');
      } else if (err?.code === 'auth/weak-password') {
        setError('Senha muito fraca. Use letras, números e símbolos.');
      } else {
        setError('Erro ao criar conta. Tente novamente.');
        console.error('[ProfessionalSignup]', err?.code || err?.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: T.s2, fontFamily: T.sans,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 16px' }}>
      <div style={{ width: '100%', maxWidth: 520 }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ fontFamily: T.serif, fontSize: 32, color: T.brand }}>
            Agenda<span style={{ color: '#B5A2D2' }}>Pro</span>
          </div>
          <p style={{ fontSize: 13, color: T.muted, marginTop: 4 }}>
            Cadastro de Profissional
          </p>
        </div>

        {/* Card */}
        <div style={{ background: '#fff', borderRadius: 16, padding: isMobile ? 20 : 32,
          boxShadow: '0 4px 20px rgba(107,88,118,0.08)' }}>

          {/* Progresso */}
          {step < 3 && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
              {['Dados profissionais', 'Dados de acesso'].map((label, i) => (
                <div key={i} style={{ flex: 1, textAlign: 'center' }}>
                  <div style={{ height: 4, borderRadius: 4, marginBottom: 6,
                    background: i + 1 <= step ? T.brand : T.border }} />
                  <span style={{ fontSize: 11, color: i + 1 <= step ? T.brand : T.muted,
                    fontWeight: i + 1 === step ? 600 : 400 }}>
                    {label}
                  </span>
                </div>
              ))}
            </div>
          )}

          {error && (
            <div style={{ padding: '10px 14px', background: '#F0DADD', borderRadius: 10,
              color: '#7A3C44', fontSize: 13, marginBottom: 16 }}>{error}</div>
          )}

          {/* Sucesso */}
          {step === 3 ? (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
              <div style={{ fontFamily: T.serif, fontSize: 24, color: T.text, marginBottom: 8 }}>
                Cadastro realizado!
              </div>
              <p style={{ color: T.text2, fontSize: 14, lineHeight: 1.6, marginBottom: 8 }}>
                Sua conta foi criada com sucesso.
              </p>
              <p style={{ color: T.muted, fontSize: 13, lineHeight: 1.6, marginBottom: 24 }}>
                Para acessar todos os recursos do sistema, peça ao administrador da sua clínica
                que vincule seu perfil ao estabelecimento.
              </p>
              <button onClick={() => navigate('/login')} style={btnPrimary(false)}>
                Ir para o login →
              </button>
            </div>

          /* Passo 1 — Dados profissionais */
          ) : step === 1 ? (
            <>
              <div style={{ fontFamily: T.serif, fontSize: 22, color: T.text, marginBottom: 4 }}>
                Dados profissionais
              </div>
              <p style={{ color: T.muted, fontSize: 13, marginBottom: 20 }}>
                Campos com * são obrigatórios.
              </p>

              <form onSubmit={handleStep1}>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: T.text2, marginBottom: 5 }}>
                    Nome completo *
                  </label>
                  <input value={nome} onChange={e => setNome(e.target.value)}
                    required placeholder="Seu nome completo" autoComplete="name"
                    style={inputStyle} />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: T.text2, marginBottom: 5 }}>
                    E-mail *
                  </label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                    required placeholder="seu@email.com" autoComplete="email"
                    style={inputStyle} />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: T.text2, marginBottom: 5 }}>
                    Telefone
                  </label>
                  <input type="tel" value={telefone} onChange={e => setTelefone(e.target.value)}
                    placeholder="(11) 99999-9999" autoComplete="tel"
                    style={inputStyle} />
                </div>
                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: T.text2, marginBottom: 8 }}>
                    Especialidade *
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    {SPECIALTIES.map(s => (
                      <button key={s.id} type="button"
                        onClick={() => setEspec(s.id)}
                        style={{ padding: '9px 12px', border: `1.5px solid ${especialidade === s.id ? T.brand : T.border}`,
                          borderRadius: 10, cursor: 'pointer',
                          background: especialidade === s.id ? T.s3 : '#fff',
                          fontFamily: T.sans, color: especialidade === s.id ? T.brand : T.text2,
                          fontSize: 13, fontWeight: especialidade === s.id ? 600 : 400,
                          textAlign: 'left', transition: 'all 0.15s' }}>
                        {s.label}
                      </button>
                    ))}
                  </div>
                  {especialidade === 'outro' && (
                    <input value={outraEspec} onChange={e => setOutraEspec(e.target.value)}
                      placeholder="Descreva sua especialidade"
                      style={{ ...inputStyle, marginTop: 10 }} />
                  )}
                </div>
                <button type="submit" style={btnPrimary(false)}>Continuar →</button>
              </form>
            </>

          /* Passo 2 — Dados de acesso */
          ) : (
            <>
              <button onClick={() => { setStep(1); setError(''); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.muted,
                  fontSize: 13, fontFamily: T.sans, padding: 0, marginBottom: 16,
                  display: 'flex', alignItems: 'center', gap: 6 }}>
                ← Voltar
              </button>
              <div style={{ fontFamily: T.serif, fontSize: 22, color: T.text, marginBottom: 4 }}>
                Dados de acesso
              </div>
              <p style={{ color: T.muted, fontSize: 13, marginBottom: 6 }}>
                Crie uma senha segura para sua conta.
              </p>
              <div style={{ marginBottom: 16, background: T.s2, borderRadius: 10, padding: '8px 12px',
                fontSize: 13, color: T.text2 }}>
                Entrando com: <strong>{email}</strong>
              </div>

              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: T.text2, marginBottom: 5 }}>
                    Senha * <span style={{ fontSize: 11, color: T.muted, fontWeight: 400 }}>(mín. 6 caracteres)</span>
                  </label>
                  <input type="password" value={senha} onChange={e => setSenha(e.target.value)}
                    required placeholder="••••••••" autoComplete="new-password"
                    style={inputStyle} />
                </div>
                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: T.text2, marginBottom: 5 }}>
                    Confirmar senha *
                  </label>
                  <input type="password" value={senha2} onChange={e => setSenha2(e.target.value)}
                    required placeholder="••••••••" autoComplete="new-password"
                    style={{ ...inputStyle, borderColor: senha2 && senha !== senha2 ? T.accent : T.border }} />
                  {senha2 && senha !== senha2 && (
                    <span style={{ fontSize: 11, color: T.accent, marginTop: 3, display: 'block' }}>
                      As senhas não coincidem
                    </span>
                  )}
                </div>
                <div style={{ padding: '12px 14px', background: '#FFF8F0', borderRadius: 10,
                  fontSize: 12, color: '#8B6914', marginBottom: 20, lineHeight: 1.5 }}>
                  Após o cadastro, solicite ao administrador da sua clínica que vincule seu perfil
                  ao estabelecimento para liberar acesso completo.
                </div>
                <button type="submit" disabled={loading} style={btnPrimary(loading)}>
                  {loading ? 'Criando conta...' : 'Criar minha conta →'}
                </button>
              </form>
            </>
          )}
        </div>

        {step < 3 && (
          <p style={{ textAlign: 'center', fontSize: 13, color: T.muted, marginTop: 16 }}>
            Já tem conta?{' '}
            <button onClick={() => navigate('/login')}
              style={{ background: 'none', border: 'none', color: T.brand, fontWeight: 600,
                cursor: 'pointer', fontFamily: T.sans, fontSize: 13 }}>
              Entrar
            </button>
          </p>
        )}
      </div>
    </div>
  );
}
